// Generic caching utility with best practices
// Supports multiple cache types, TTL, and easy expansion

export interface CacheItem<T> {
    data: T;
    timestamp: number;
    ttl: number; // Time to live in milliseconds
}

export interface CacheConfig {
    ttl: number; // Default TTL in milliseconds
    keyPrefix: string; // Prefix for localStorage keys
    maxSize?: number; // Optional: max items in cache
}

export class Cache<T> {
    private config: CacheConfig;
    private memoryCache = new Map<string, CacheItem<T>>();

    constructor(config: CacheConfig) {
        this.config = config;
    }

    /**
     * Get item from cache (checks memory first, then localStorage)
     */
    get(key: string): T | null {
        const fullKey = `${this.config.keyPrefix}_${key}`;

        // Check memory cache first (fastest)
        const memoryItem = this.memoryCache.get(key);
        if (memoryItem && this.isValid(memoryItem)) {
            return memoryItem.data;
        }

        // Check localStorage (persistent)
        try {
            const stored = localStorage.getItem(fullKey);
            if (stored) {
                const item: CacheItem<T> = JSON.parse(stored);
                if (this.isValid(item)) {
                    // Restore to memory cache for faster access
                    this.memoryCache.set(key, item);
                    return item.data;
                } else {
                    // Clean up expired item
                    this.remove(key);
                }
            }
        } catch (error) {
            console.warn(`Cache: Failed to read ${fullKey}:`, error);
            this.remove(key);
        }

        return null;
    }

    /**
     * Set item in cache (both memory and localStorage)
     */
    set(key: string, data: T, customTtl?: number): void {
        const ttl = customTtl || this.config.ttl;
        const item: CacheItem<T> = {
            data,
            timestamp: Date.now(),
            ttl,
        };

        const fullKey = `${this.config.keyPrefix}_${key}`;

        // Store in memory cache
        this.memoryCache.set(key, item);

        // Store in localStorage for persistence
        try {
            localStorage.setItem(fullKey, JSON.stringify(item));
        } catch (error) {
            console.warn(`Cache: Failed to store ${fullKey}:`, error);
            // If localStorage is full, try to clean up old items
            this.cleanup();
            try {
                localStorage.setItem(fullKey, JSON.stringify(item));
            } catch (retryError) {
                console.error(`Cache: Failed to store ${fullKey} after cleanup:`, retryError);
            }
        }

        // Enforce max size if configured
        if (this.config.maxSize && this.memoryCache.size > this.config.maxSize) {
            this.evictOldest();
        }
    }

    /**
     * Remove item from cache
     */
    remove(key: string): void {
        const fullKey = `${this.config.keyPrefix}_${key}`;
        this.memoryCache.delete(key);

        try {
            localStorage.removeItem(fullKey);
        } catch (error) {
            console.warn(`Cache: Failed to remove ${fullKey}:`, error);
        }
    }

    /**
     * Clear all items from this cache
     */
    clear(): void {
        this.memoryCache.clear();

        try {
            // Remove all items with our prefix from localStorage
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.config.keyPrefix)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (error) {
            console.warn('Cache: Failed to clear localStorage:', error);
        }
    }

    /**
     * Get cache statistics for debugging
     */
    getStats(): { memorySize: number; localStorageKeys: number } {
        let localStorageKeys = 0;
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.config.keyPrefix)) {
                    localStorageKeys++;
                }
            }
        } catch (error) {
            console.warn('Cache: Failed to get localStorage stats:', error);
        }

        return {
            memorySize: this.memoryCache.size,
            localStorageKeys,
        };
    }

    /**
     * Check if cache item is still valid
     */
    private isValid(item: CacheItem<T>): boolean {
        return Date.now() - item.timestamp < item.ttl;
    }

    /**
     * Remove oldest items from memory cache
     */
    private evictOldest(): void {
        if (this.memoryCache.size === 0) return;

        let oldestKey = '';
        let oldestTimestamp = Date.now();

        for (const [key, item] of this.memoryCache.entries()) {
            if (item.timestamp < oldestTimestamp) {
                oldestTimestamp = item.timestamp;
                oldestKey = key;
            }
        }

        if (oldestKey) {
            this.remove(oldestKey);
        }
    }

    /**
     * Clean up expired items from localStorage
     */
    private cleanup(): void {
        try {
            const keysToRemove: string[] = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.config.keyPrefix)) {
                    try {
                        const stored = localStorage.getItem(key);
                        if (stored) {
                            const item: CacheItem<T> = JSON.parse(stored);
                            if (!this.isValid(item)) {
                                keysToRemove.push(key);
                            }
                        }
                    } catch (error) {
                        // If we can't parse it, remove it
                        keysToRemove.push(key);
                    }
                }
            }

            keysToRemove.forEach(key => localStorage.removeItem(key));
            console.log(`Cache: Cleaned up ${keysToRemove.length} expired items`);
        } catch (error) {
            console.warn('Cache: Failed to cleanup expired items:', error);
        }
    }
}

// Utility function to create cache instances with common configurations
export const createCache = <T>(name: string, ttlMinutes: number = 30, maxSize?: number): Cache<T> => {
    return new Cache<T>({
        ttl: ttlMinutes * 60 * 1000, // Convert minutes to milliseconds
        keyPrefix: `loppehub_${name}`,
        maxSize,
    });
};